import styled from "styled-components";

import { FONTS, QUERIES, SPACING } from "../../../../../utils/constants";
import { SECTION_GAP, TOP_NAV_HEIGHT } from "../../constants";
import { SectionCardRow } from "../SectionCard/styles";
import Button from "../../../../../components/Button";

export const Panel = styled.section`
  overflow: hidden;
  border: 1px solid var(--color-card-border);
  border-radius: 12px;
  background: var(--color-card-background);
  box-shadow: var(--shadow-card-rest);
  font-family: ${FONTS.serif};
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.sm + SPACING.xs}px;
  padding: ${SPACING.md + SPACING.xxs / 2}px ${SPACING.md + SPACING.xxs}px
    ${SPACING.md - SPACING.xxs - SPACING.xxs / 2}px;
  border-bottom: 1px solid var(--color-card-border);
`;

export const HeaderText = styled.div`
  min-width: 0;
`;

export const Heading = styled.h2`
  margin: 0;
  color: var(--color-text-default);
  font-family: ${FONTS.serif};
  font-size: var(--text-xl);
  font-weight: 700;
  letter-spacing: -0.015em;
  line-height: 1.2;
`;

export const HeadingNote = styled.p`
  margin: ${SPACING.xxs}px 0 0;
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  line-height: 1.35;
`;

export const ResetButton = styled(Button)`
  flex-shrink: 0;

  @media ${QUERIES.tabletLargeAndDown} {
    min-height: 44px;
  }
`;

export const NoticeInset = styled.div`
  padding: ${SPACING.md}px ${SPACING.md + SPACING.xxs}px 0;
`;

export const Body = styled.div`
  padding: ${SPACING.md}px ${SPACING.md + SPACING.xxs}px;
`;

export const Hero = styled.div`
  padding-bottom: ${SPACING.md - SPACING.xxs / 2}px;
  border-bottom: 1px solid var(--color-card-border);
`;

export const HeroLabel = styled.span`
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

export const HeroAmount = styled.strong`
  display: block;
  margin-top: ${SPACING.xxs + SPACING.xxs / 2}px;
  color: var(--color-text-default);
  font-family: ${FONTS.serif};
  font-size: var(--text-xxxl);
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
`;

export const HeroCaption = styled.p`
  margin: ${SPACING.xs + SPACING.xxs / 2}px 0 0;
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  line-height: 1.4;
`;

export const Metrics = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 0;
`;

export const Metric = styled.div<{ $spansRow?: boolean }>`
  min-width: 0;
  grid-column: ${({ $spansRow }) => ($spansRow ? "1 / -1" : "auto")};
  padding: ${SPACING.sm + SPACING.xs}px 0;
  border-bottom: 1px solid var(--color-card-border);

  &:nth-child(even) {
    padding-left: ${SPACING.md - SPACING.xxs / 2}px;
    border-left: 1px solid var(--color-card-border);
  }

  dt {
    display: flex;
    align-items: center;
    gap: ${SPACING.xs}px;
    color: var(--color-text-muted);
    font-size: var(--text-xs);
  }

  dd {
    margin: ${SPACING.xxs}px 0 0;
    color: var(--color-text-default);
    font-size: var(--text-md);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
`;

export const ConcentrationSection = styled.div`
  padding-top: ${SPACING.md}px;

  > strong {
    display: block;
    margin-bottom: ${SPACING.sm}px;
    color: var(--color-text-default);
    font-size: var(--text-sm);
  }
`;

export const ConcentrationRows = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

// Fixed `ch` columns rather than content-sized ones, so every row's bar starts and ends on the same line.
export const ConcentrationRow = styled.li`
  display: grid;
  grid-template-columns: 8ch 1fr 6ch;
  align-items: center;
  gap: ${SPACING.sm}px;
  margin-top: ${SPACING.sm}px;
  color: var(--color-text-secondary);
  font-size: var(--text-xs);

  span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span:last-child {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`;

export const ConcentrationTrack = styled.div`
  height: 4px;
  overflow: hidden;
  border-radius: 2px;
  background: var(--color-border-light);
`;

export const ConcentrationFill = styled.div<{ $share: number; $color: string }>`
  width: ${({ $share }) => `${$share}%`};
  height: 100%;
  background: ${({ $color }) => $color};
`;

export const Note = styled.p`
  margin: ${SPACING.md}px 0 0;
  padding: ${SPACING.sm}px;
  border: 1px solid var(--color-status-warning-border);
  border-radius: 8px;
  background: var(--color-status-warning-background);
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  line-height: 1.45;
`;

export const Message = styled.p`
  margin: 0;
  padding: ${SPACING.lg}px ${SPACING.sm}px;
  color: var(--color-text-secondary);
  font-size: var(--text-base);
  line-height: 1.45;
  text-align: center;
`;

// Sticky only in the two-column band; stacked below the workspace it has nothing to follow.
// The top margin mirrors the Holdings card's own, so both columns start on one line.
export const Rail = styled.aside`
  min-width: 0;

  @media ${QUERIES.laptopLargeAndUp} {
    margin-top: ${SECTION_GAP}px;
    position: sticky;
    top: ${TOP_NAV_HEIGHT + SPACING.md}px;
  }
`;

// A card-list row so it inherits the band's inset, hairline and last-row corners.
export const InlinePanel = styled(SectionCardRow)`
  gap: ${SPACING.md}px;
  background: color-mix(in srgb, var(--color-primary) 6%, transparent);
  font-family: ${FONTS.serif};
`;

export const InlineHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.md}px;
`;

export const InlineHeading = styled.h3`
  margin: 0;
  color: var(--color-text-default);
  font-family: ${FONTS.serif};
  font-size: var(--text-lg);
  font-weight: 700;
  line-height: 1.2;
`;
